import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Landmark, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SettingsLayout from "./SettingsLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { apiError } from "@/lib/api";

export default function RepaymentAccount() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const returnTo = useLocation().state?.returnTo;
  const [banks, setBanks] = useState([]);
  const [current, setCurrent] = useState(null);
  const [editing, setEditing] = useState(false);
  const [bankCode, setBankCode] = useState("");
  const [acctNum, setAcctNum] = useState("");
  const [resolved, setResolved] = useState(null); // {account_name, account_masked}
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [b, a] = await Promise.all([
        api.get("/repayment/banks"),
        api.get("/repayment/account"),
      ]);
      setBanks(b.data.banks || []);
      setCurrent(a.data.account || null);
      if (!a.data.account) setEditing(true);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => { setResolved(null); }, [bankCode, acctNum]);

  const bankName = banks.find((x) => x.code === bankCode)?.name;

  const verify = async () => {
    if (!bankCode || acctNum.trim().length !== 10) {
      toast.error("Select a bank and enter a 10-digit account number.");
      return;
    }
    setVerifying(true);
    try {
      const { data } = await api.post("/repayment/resolve", { bank_code: bankCode, account_number: acctNum.trim() });
      setResolved(data);
    } catch (e) {
      setResolved(null);
      toast.error(apiError(e, "We couldn't verify that account. Please check and try again."));
    } finally {
      setVerifying(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/repayment/account", {
        bank_code: bankCode, bank_name: bankName, account_number: acctNum.trim(),
      });
      setCurrent({ ...data.account, account_number: acctNum.trim() });
      setEditing(false);
      setResolved(null); setBankCode(""); setAcctNum("");
      toast.success("Repayment account saved");
      qc.invalidateQueries({ queryKey: ["repayment-account"] });
      if (returnTo) navigate(-1);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsLayout title="Repayment account" subtitle="Add the bank account where you would like to receive debt repayments" testId="repayment-account-page">
      {current && !editing && (
        <div data-testid="repayment-account-current" className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-emerald-700 text-sm font-bold mb-3">
            <CheckCircle2 className="h-4 w-4" /> Account verified
          </div>
          <p className="font-display text-lg font-extrabold text-foreground">{current.account_name}</p>
          <p className="text-sm text-muted-foreground mt-0.5">{current.bank_name} · {current.account_masked}</p>
          <Button data-testid="repayment-change-btn" variant="outline" className="mt-4 h-11 rounded-xl w-full"
            onClick={() => { setEditing(true); setBankCode(""); setAcctNum(""); setResolved(null); }}>
            Change repayment account
          </Button>
          <p className="mt-3 text-xs text-muted-foreground">
            Debii never receives or holds your money — repayments go directly to this bank account.
          </p>
        </div>
      )}

      {editing && (
        <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
          <div className="flex items-center gap-2 text-primary text-sm font-bold">
            <Landmark className="h-4 w-4" /> Nigerian bank account (NGN)
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Bank</label>
            <Select value={bankCode} onValueChange={setBankCode}>
              <SelectTrigger data-testid="repayment-bank-select" className="mt-1 h-11 rounded-xl">
                <SelectValue placeholder="Select your bank" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {banks.map((b) => (
                  <SelectItem key={`${b.code}-${b.name}`} value={b.code}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Account number</label>
            <Input data-testid="repayment-account-input" inputMode="numeric" maxLength={10}
              value={acctNum} onChange={(e) => setAcctNum(e.target.value.replace(/\D/g, ""))}
              placeholder="0123456789" className="mt-1 h-11 rounded-xl tabular" />
          </div>

          {resolved ? (
            <div data-testid="repayment-resolved" className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
              <div className="flex items-center gap-2 text-emerald-700 text-sm font-bold"><CheckCircle2 className="h-4 w-4" /> Account found</div>
              <p className="mt-2 font-display text-lg font-extrabold text-emerald-900">{resolved.account_name}</p>
              <p className="text-sm text-emerald-700">{bankName} · {resolved.account_masked}</p>
              <p className="mt-2 text-sm text-emerald-800">Is this the correct account?</p>
              <Button data-testid="repayment-confirm-save" onClick={save} disabled={saving} className="mt-3 w-full h-11 rounded-xl">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm account"}
              </Button>
            </div>
          ) : (
            <Button data-testid="repayment-verify-btn" onClick={verify} disabled={verifying} className="w-full h-11 rounded-xl">
              {verifying ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</> : "Verify account"}
            </Button>
          )}
          {current && (
            <button data-testid="repayment-cancel-btn" className="w-full text-center text-sm text-muted-foreground" onClick={() => (returnTo ? navigate(-1) : setEditing(false))}>Cancel</button>
          )}
        </div>
      )}
    </SettingsLayout>
  );
}
